/**
 * PTV2-1327 — the single global "autosave on/off" preference.
 *
 * One setting drives card autosave in BOTH contexts (patient record + pedigree node editor): when ON
 * the editable surfaces behave like Google Docs (silent debounced autosave + a "Saved" status, no
 * Save/Discard buttons, no success toast); when OFF they show explicit Save / Discard. Persisted to
 * localStorage so the choice sticks across reloads. Default ON.
 *
 * Zustand (like the rest of the app) so components re-render when it flips. The pedigree toolbar's
 * autosave toggle mirrors into this store (see `useAutosaveToggleSync`), so either control is "the"
 * toggle.
 */
import { create } from 'zustand';

const STORAGE_KEY = 'ptv2.autosaveEnabled';

function readInitial(): boolean {
  try {
    const raw = typeof localStorage === 'undefined' ? null : localStorage.getItem(STORAGE_KEY);
    // Default ON: only an explicit "false" disables it.
    return raw !== 'false';
  } catch {
    return true;
  }
}

interface AutosaveEnabledState {
  readonly enabled: boolean;
  readonly setEnabled: (enabled: boolean) => void;
}

export const useAutosaveEnabledStore = create<AutosaveEnabledState>()((set) => ({
  enabled: readInitial(),
  setEnabled: (enabled) => {
    try {
      if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEY, String(enabled));
    } catch {
      // storage unavailable (private mode / SSR) — keep the in-memory value.
    }
    set({ enabled });
  },
}));

/** Imperative setter for non-React callers (e.g. the pedigree-toolbar sync). */
export function setAutosaveEnabled(enabled: boolean): void {
  useAutosaveEnabledStore.getState().setEnabled(enabled);
}
